import { memoize } from 'lodash';
import { QUESTION_TYPES, QUESTION_RESPONSE_STATUS, REVIEW_STATUS } from '../constants';

export const getQuestionFromQuestionId = (assignment, questionId) => {
  return assignment?.questions?.find((q) => q.id === questionId);
};

/**
 * Teacher setting: "Show solutions after assignment submission".
 * Explicit false hides solutions. Missing/legacy values default to showing them.
 */
export const shouldRevealSolutions = (source) => {
  if (!source || typeof source !== 'object') return true;
  if (typeof source.revealSolutions === 'boolean') return source.revealSolutions;
  const settings = source.settings || source.assignment?.settings;
  if (typeof settings?.revealSolutions === 'boolean') return settings.revealSolutions;
  return true;
};

const isNonEmptyText = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number' && Number.isFinite(value)) return true;
  if (typeof value !== 'string') return false;
  const stripped = value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped.length > 0;
};

const hasNonEmptyValue = (value) => {
  if (Array.isArray(value)) return value.some(hasNonEmptyValue);
  if (value instanceof Map) return [...value.values()].some(hasNonEmptyValue);
  if (value && typeof value === 'object') return Object.values(value).some(hasNonEmptyValue);
  return isNonEmptyText(value);
};

const hasUploadedFile = (uploadedFile) =>
  Boolean(uploadedFile && (uploadedFile instanceof File || uploadedFile.fileKey));

const extractBlankIds = (html) => {
  if (!html) return [];
  const ids = [];
  const regex = /blankid=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    ids.push(match[1]);
  }
  return ids;
};

const getBlankValue = (answers, blankId) => {
  if (!answers) return undefined;
  if (answers instanceof Map) return answers.get(blankId);
  return answers[blankId];
};

const hasCompletedFibAnswers = (answers, question) => {
  const blankIds = extractBlankIds(question?.elements?.[0]?.html);
  if (blankIds.length > 0) {
    return blankIds.every((blankId) => hasNonEmptyValue(getBlankValue(answers, blankId)));
  }
  return hasNonEmptyValue(answers);
};

export const hasAttemptedQuestion = ({ type, answer, answers, uploadedFile, question } = {}) => {
  if (
    type === QUESTION_TYPES.FILE_UPLOAD ||
    type === QUESTION_TYPES.AUDIO ||
    type === QUESTION_TYPES.VIDEO
  ) {
    return hasUploadedFile(uploadedFile);
  }

  if (type === QUESTION_TYPES.FIB) return hasCompletedFibAnswers(answers, question);

  return hasNonEmptyValue(answer);
};
/**
 * Marks for the answer on its own terms.
 *
 * Inside a choice group the stored `score`/`maxScore` are the share of the group's marks the
 * answer actually earned - 0 out of 0 when a better answer displaced it. Review has to judge
 * the answer itself, so it reads `rawScore`/`rawMaxScore`, which the grader preserves. Both
 * views coincide for an ordinary question.
 */
const getRawMarks = (answerScoreData) => ({
  score: Number(answerScoreData?.rawScore ?? answerScoreData?.score),
  maxScore: Number(answerScoreData?.rawMaxScore ?? answerScoreData?.maxScore)
});

const isFullScore = (answerScoreData) => {
  const { score, maxScore } = getRawMarks(answerScoreData);

  return (
    Number.isFinite(score) &&
    Number.isFinite(maxScore) &&
    maxScore > 0 &&
    score === maxScore
  );
};

const isMcqAnswerCorrect = (question, questionResponse) => {
  const selectedIds = (questionResponse?.answer || []).filter(Boolean);
  const correctIds = (question?.options || []).filter((option) => option.isCorrect).map((option) => option.id);

  if (!selectedIds.length || !correctIds.length) {
    return false;
  }

  return (
    selectedIds.length === correctIds.length &&
    correctIds.every((id) => selectedIds.includes(id))
  );
};

export const getAnswerStatus = (question, answerScoreData) => {
  const type = question?.type;
  switch (type) {
    case QUESTION_TYPES.MCQ: {
      return isFullScore(answerScoreData) ? REVIEW_STATUS.CORRECT : REVIEW_STATUS.INCORRECT;
    }
    case QUESTION_TYPES.FIB: {
      if (!answerScoreData || getRawMarks(answerScoreData).score === 0) {
        return REVIEW_STATUS.INCORRECT;
      } else if (isFullScore(answerScoreData)) {
        return REVIEW_STATUS.CORRECT;
      } else {
        return REVIEW_STATUS.PARTIAL;
      }
    }
    // AI evaluation question types
    case QUESTION_TYPES.SUBJECTIVE:
    case QUESTION_TYPES.FILE_UPLOAD:
    case QUESTION_TYPES.AUDIO:
    case QUESTION_TYPES.VIDEO:
    case QUESTION_TYPES.CODE: {
      if (!answerScoreData) {
        return REVIEW_STATUS.INCORRECT;
      }

      const { score } = getRawMarks(answerScoreData);
      if (score === 0) {
        return REVIEW_STATUS.INCORRECT;
      } else if (isFullScore(answerScoreData)) {
        return REVIEW_STATUS.CORRECT;
      } else {
        return REVIEW_STATUS.PARTIAL;
      }
    }

    default:
      return REVIEW_STATUS.INCORRECT;
  }
};

export const getReviewAnswerStatus = (question, questionResponse, answerScoreData) => {
  if (questionResponse?.status !== QUESTION_RESPONSE_STATUS.ANSWERED) {
    return REVIEW_STATUS.INCORRECT;
  }

  const status = getAnswerStatus(question, answerScoreData);
  if (status === REVIEW_STATUS.CORRECT || status === REVIEW_STATUS.PARTIAL) {
    return status;
  }

  if (question?.type === QUESTION_TYPES.MCQ && isMcqAnswerCorrect(question, questionResponse)) {
    return REVIEW_STATUS.CORRECT;
  }

  return status;
};

// Return map of question ids mapping to reading comprehension question ids
const getSubQuestionsToRCMap = memoize((assignmentData) => {
  const mapping = {};
  assignmentData.assignment.questions.forEach((question) => {
    if (question.type === QUESTION_TYPES.READING_COMPREHENSION) {
      question.subQuestions.forEach((subQuestion) => {
        mapping[subQuestion] = question.id;
      });
    }
  });

  return mapping;
});

const getReadingComprehensionQuestionData = memoize((assignmentData) => {
  return Object.fromEntries(
    assignmentData.assignment.questions.map((q) => {
      if (q.type === QUESTION_TYPES.READING_COMPREHENSION) {
        return [
          q.id,
          {
            elements: q.elements
          }
        ];
      } else {
        return [q.id, undefined];
      }
    })
  );
});

const attachReadingComprehensionQuestionRef = (question, assignmentData) => {
  if (!question?.id || !assignmentData) {
    return;
  }

  // We need two things here: a map of reading comprehension question ids vs their data
  // and we need a map of question ids mapping to reading comprehension question ids
  const subQuestionToRCMap = getSubQuestionsToRCMap(assignmentData);

  // Find the reading comprehension questions from the assignment first
  const readingComprehensionQuestionData = getReadingComprehensionQuestionData(assignmentData);

  // If the question is a part of reading comprehension, we need to attach the reading comprehension question ref to it
  const rcQuestionId = subQuestionToRCMap[question.id];
  if (rcQuestionId) {
    question.readingComprehensionQuestionRef = readingComprehensionQuestionData[rcQuestionId];
  }
};

const getTotalQuestions = (assignmentData) => {
  // studentResponse is already expanded (RC parents → sub-questions) and is the
  // source of truth for how many questions the student can navigate.
  if (assignmentData?.studentResponse?.length) {
    return assignmentData.studentResponse.length;
  }

  return (
    assignmentData?.assignment?.questions?.reduce((count, question) => {
      if (question.type === QUESTION_TYPES.READING_COMPREHENSION) {
        return count + (question.subQuestions?.length || 0);
      }
      const isRcSubQuestion = assignmentData.assignment.questions.some(
        (q) =>
          q.type === QUESTION_TYPES.READING_COMPREHENSION && q.subQuestions?.includes(question.id)
      );
      return isRcSubQuestion ? count : count + 1;
    }, 0) || 0
  );
};

const getAssignmentDetails = ({ assignmentData, questionId }) => {
  if (!assignmentData) {
    return {};
  }
  const { assignment, studentResponse } = assignmentData;
  const currentQuestion = getQuestionFromQuestionId(assignment, questionId);
  const currentQuestionResponse = studentResponse.find((q) => q.questionId === questionId);

  // If the current question is a part of reading comprehension, we need to attach the reading comprehension question ref to it
  attachReadingComprehensionQuestionRef(currentQuestion, assignmentData);

  // Prefer the live response list — it is the source of truth for navigation order
  // (RC parents are already expanded into sub-questions).
  const totalQuestions = studentResponse?.length || getTotalQuestions(assignmentData);

  // Finding the next & previous question
  let nextQuestionId = null;
  let previousQuestionId = null;
  let isLastQuestion = false;
  let isFirstQuestion = false;
  const currentQuestionIndex = studentResponse.findIndex(
    (question) => question.questionId === questionId
  );

  if (currentQuestionIndex >= 0 && currentQuestionIndex < totalQuestions - 1) {
    nextQuestionId = studentResponse[currentQuestionIndex + 1].questionId;
  } else if (currentQuestionIndex >= 0) {
    isLastQuestion = true;
  }

  if (currentQuestionIndex > 0) {
    previousQuestionId = studentResponse[currentQuestionIndex - 1].questionId;
  } else {
    isFirstQuestion = true;
  }

  const answerScoreData = assignmentData.result?.scoreMap?.[questionId];

  return {
    isFirstQuestion,
    isLastQuestion,
    currentQuestion,
    currentQuestionIndex,
    currentQuestionResponse,
    nextQuestionId,
    previousQuestionId,
    name: assignment?.name,
    answerScoreData
  };
};

export { getAssignmentDetails };

export const getRcContext = (questionId, assignmentData) => {
  if (!questionId || !assignmentData?.assignment?.questions) return null;

  const rcQuestion = assignmentData.assignment.questions.find(
    (q) => q.type === QUESTION_TYPES.READING_COMPREHENSION && q.subQuestions?.includes(questionId)
  );

  if (!rcQuestion) return null;

  const subIndex = rcQuestion.subQuestions.indexOf(questionId);
  if (subIndex === -1) return null;

  return {
    rcId: rcQuestion.id,
    subIndex,
    totalSub: rcQuestion.subQuestions.length,
    subQuestionIds: rcQuestion.subQuestions
  };
};

export const getAnswerColorTheme = (answerStatus) => {
  switch (answerStatus) {
    case REVIEW_STATUS.CORRECT:
      return { color: 'green', bg: 'green.100', text: 'Wohoo! Good work — you are right!' };
    case REVIEW_STATUS.PARTIAL:
      return {
        color: 'orange',
        bg: 'orange.100',
        text: 'Oops! You missed some answers - check out the solutions!'
      };
    case REVIEW_STATUS.INCORRECT:
      return {
        color: 'red',
        bg: 'red.100',
        text: 'Oops! You missed this — check out the solution!'
      };
    case REVIEW_STATUS.SKIPPED:
      return {
        color: 'gray',
        bg: 'gray.100',
        text: 'Seems you skipped it — check out the solution!'
      };
    default:
      return {
        color: 'gray',
        bg: 'gray.100',
        text: 'Seems you skipped it — check out the solution!'
      };
  }
};
